import { FormControl, MenuItem, Box, Select, Avatar, IconButton, Typography,} from "@mui/material";
import OutlinedInput from "@mui/material/OutlinedInput";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EditIcon from "@mui/icons-material/Edit";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import NotesIcon from "@mui/icons-material/Notes";
import { extractTextFromHTML } from "../../utils/textUtils";
import { useLocation } from "react-router-dom";
import { createEntity, fetchEntityById } from "../../apis/apiService";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useNavigate } from "react-router-dom";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import RichText from "../../components/common/RichText";

const getInitials = (name) => {
  if (!name) return "";
  const parts = name.split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name[0].toUpperCase();
};

const DetailField = ({ icon, label, value, valueColor, isFullWidth }) => {
  const isDark = document.body.classList.contains("bg-dark");
  const borderColor = isDark ? "rgba(255, 255, 255, 0.08)" : "#f0f0f0";
  
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        py: 2,
        borderBottom: isFullWidth ? "none" : `1px solid ${borderColor}`,
        minHeight: "65px",
        gridColumn: isFullWidth ? { xs: "span 1", md: "span 2" } : "auto",
      }}
    >
      {/* Icon Square Wrapper */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 36,
          height: 36,
          borderRadius: "8px",
          backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "#f5f5f5",
          color: isDark ? "rgba(255, 255, 255, 0.7)" : "#555555",
          mr: 2,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      
      {/* Label */}
        <Typography
        variant="body2"
          sx={{
            color: isDark ? "rgba(255, 255, 255, 0.7)" : "text.secondary",
            fontWeight: 500,
          mr: 2,
          minWidth: isFullWidth ? "100px" : "auto",
          flexGrow: isFullWidth ? 0 : 1,
          }}
        >
          {label}
        </Typography>
      
      {/* Value */}
        <Box
          sx={{
            fontWeight: 600,
          fontSize: "0.95rem",
            color: valueColor || (isDark ? "#ffffff" : "text.primary"),
          textAlign: isFullWidth ? "left" : "right",
          flexGrow: isFullWidth ? 1 : 0,
            wordBreak: "break-word",
          }}
        >
          {value}
        </Box>
      </Box>
  );
};

const OTSheetDetail = ({ handleClosePanel }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const date = queryParams.get("date");
  const [loading, setLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState("");
  const [showDetail, setShowDetail] = useState([]);
  const [notes, setNotes] = useState("");
  const [logDate, setLogDate] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDailyLog, setShowDailyLog] = useState(false);
  const [selectedTimesheetType, setSelectedTimesheetType] = useState("");
  const [totalHours, setTotalHours] = useState("");
  const [approvalStatus, setApprovalStatus] = useState("");
  const [timePeriod, setTimePeriod] = useState("N/A");
  const [projectTitle, setProjectTitle] = useState("");
  const [taskTitle, setTaskTitle] = useState("");
  const [otType, setOtType] = useState("");
  const [creatorName, setCreatorName] = useState("");
  const [isEditable, setIsEditable] = useState(false);
   const navigate = useNavigate();
   const [themeKey, setThemeKey] = useState(document.body.classList.contains("bg-dark") ? "dark" : "light");
   const id = new URLSearchParams(window.location.search).get("id");

  dayjs.extend(customParseFormat);

  
    const handleBack = () => {
    navigate("/OT-Sheet/Pending-Sheet"); // ✅ navigate to route
  };

  useEffect(() => {
  const observer = new MutationObserver(() => {
    setThemeKey(document.body.classList.contains("bg-dark") ? "dark" : "light");
  });

  observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });

  return () => observer.disconnect();
}, []);

const getStatusStyle = (status) => {
  const isDark = document.body.classList.contains("bg-dark");

  const statusStyles = {
    APPROVED: {
      backgroundColor: isDark ? "#133a3f" : "#d1ecf1",
      color: isDark ? "#5ed1d1" : "#0c5460",
      borderColor: isDark ? "#2d6c72" : "#bee5eb",
    },
    PENDING: {
      backgroundColor: isDark ? "#3a2f52" : "#e2d6f6",
      color: isDark ? "#cbb8ff" : "#6f42c1",
      borderColor: isDark ? "#5c4c7a" : "#c8b6e2",
    },
    REJECTED: {
      backgroundColor: isDark ? "#4a2020" : "#f8d7da",
      color: isDark ? "#ff9c9c" : "#a31616",
      borderColor: isDark ? "#7b3a3a" : "#f5c6cb",
    },
  };

  return (
    statusStyles[status] || {
      backgroundColor: isDark ? "#1e1e1e" : "#fff",
      color: isDark ? "#f2f1f1" : "#000",
      borderColor: isDark ? "#444" : "#ccc",
    }
  );
};

  const convertTo12HourFormat = (time) => {
    if (!time) return "N/A";
    return dayjs(time, "HH:mm:ss").format("h:mm A");
  };

  const otsheetinfo = async () => {
    
    // console.log("id from the params",id);    
    
    if (!id) return toast.warn("No ID found in URL");

    setLoading(true);
    try {
      const response = await fetchEntityById("timesheet/view", id);
      // console.log("response data", response?.data);

      if (response?.data) {
        setSelectedTimesheetType(response?.data?.timesheet_type);

        if (Array.isArray(response.data)) {
          setShowDetail(response.data);

          if (response.data[0]) {
            const first = response.data[0];
            setNotes(first.notes || "");
            setLogDate(first.log_date || "");
            setSelectedDate(dayjs(first.log_date));

            const formattedTime =
              first.start_time && first.end_time
                ? `${convertTo12HourFormat(
                    first.start_time
                  )} - ${convertTo12HourFormat(first.end_time)}`
                : "N/A";

            setTimePeriod(formattedTime);

            const isStartValid = dayjs(first.start_time).isValid();
            const isEndValid = dayjs(first.end_time).isValid();
            setShowDailyLog(!(isStartValid && isEndValid));
          }
        } else if (typeof response.data === "object") {
          const data = response.data;
          setShowDetail([data]);
          setNotes(data.notes || "");
          setLogDate(data.log_date || "");
          setSelectedDate(dayjs(data.log_date));
          setApprovalStatus(data?.approval_status);
          setTotalHours(data.hours_in_text);
          setProjectTitle(data.project_title);
          setTaskTitle(data.task_general_issue);
          setOtType(data.timesheet_type);
          setCreatorName(data.added_name);
          setIsEditable(data.is_editable);
          const formattedTime =
            data.start_time && data.end_time
              ? `${convertTo12HourFormat(
                  data.start_time
                )} - ${convertTo12HourFormat(data.end_time)}`
              : "N/A";

          setTimePeriod(formattedTime); // ✅ new state here

          const isStartValid = dayjs(data.start_time).isValid();
          const isEndValid = dayjs(data.end_time).isValid();
          setShowDailyLog(!(isStartValid && isEndValid));
        } else {
          toast.warn("Unexpected data format");
          setShowDetail([]);
        }
      } else {
        toast.warn("Unexpected response format");
        setShowDetail([]);
      }
    } catch (err) {
      console.error("Error in fetching timesheet detail:", err);
      setShowDetail([]);
    } finally {
      setLoading(false);
    }
  };

  // 👇 Call API once on component mount
  useEffect(() => {
    otsheetinfo();
  }, []);

  // ✅ Get the passed state (row)
  useEffect(() => {
    if (location.state?.row) {
      setSelectedRow(location.state.row);
    }
  }, [location.state]);

  // console.log("selectedRow_value:", selectedRow);
  // console.log("date_value:", date);
  // if (!selectedRow) return null;

  const handleUpdateInlineStatus = async (event, recordId) => {
    const newStatus = event.target.value;

    setApprovalStatus(newStatus);

    // ✅ Optimistically update UI first
    setSelectedRow((prev) => ({
      ...prev,
      status: newStatus,
    }));

    try {
      await updateApprovalStatus(recordId, newStatus);
      // console.log(`Status updated successfully for record ${recordId}`);
    } catch (error) {
      console.error("Error updating status:", error);
      // Optionally revert UI on error
      setSelectedRow((prev) => ({
        ...prev,
        status: selectedRow.status, // revert to old
      }));
    }
  };

  const updateApprovalStatus = async (recordIds, approvalStatus) => {
    setLoading(true);
    const requestedData = {
      record_ids: Array.isArray(recordIds) ? recordIds : [recordIds],
      approval_status: approvalStatus,
    };

    try {
      const response = await createEntity("ot/update", requestedData);
      // console.log("Update response:", response);
      if (response.status_code == 200) {
        toast.success("status updated successfully");
      }
      return response;
    } catch (error) {
      console.error("Failed to update approval status:", error);
      toast.error(error?.message ||"something went wrong");
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const isDark = document.body.classList.contains("bg-dark");

  return (
    <Box sx={{ p: 1, backgroundColor: "transparent" }}>
      {/* Outer Card Container */}
      <Box
        sx={{
          backgroundColor: isDark ? "#1e1e1e" : "#ffffff",
          borderRadius: "10px",
          boxShadow: isDark 
            ? "0 4px 20px rgba(0, 0, 0, 0.4)" 
            : "0 4px 20px rgba(0, 0, 0, 0.05)",
          border: isDark ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid #f0f0f0",
          p: 4,
          position: "relative",
          pt: 5, // optimal top padding for the back button
        }}
      >
        {/* Back Button on top left (Icon only, 5px border radius) */}
        <IconButton
          onClick={handleBack}
          sx={{
            position: "absolute",
            top: 20,
            left: 20,
            borderRadius: "5px",
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.08)" : "#f5f5f5",
            color: isDark ? "#ffffff" : "#333333",
            width: "36px",
            height: "36px",
            "&:hover": {
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.15)" : "#e0e0e0",
            },
          }}
        >
          <ArrowBackIosIcon sx={{ fontSize: 16, ml: "6px" }} />
        </IconButton>

        {/* Profile Card Header (Centered Logo & Name) */}
        <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              fontSize: "28px",
              fontWeight: 700,
              background: "linear-gradient(135deg, #02b298 0%, #00d2b4 100%)",
              color: "#ffffff",
              boxShadow: "0 4px 12px rgba(2, 178, 152, 0.3)",
              mb: 2,
            }}
          >
            {getInitials(selectedRow?.name || creatorName)}
          </Avatar>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}>
              {selectedRow?.name || creatorName || "Employee"}
            </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", textTransform: "uppercase", letterSpacing: "1px", fontWeight: 600 }}>
            Overtime Log Profile Details
            </Typography>
          </Box>

        {/* 2-Column Detail List Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            columnGap: 6,
            rowGap: 0,
          }}
        >
          {/* Left Column */}
          <Box>
            <DetailField
              icon={<AccessTimeIcon sx={{ fontSize: 18 }} />}
              label="OT Hours"
              value={totalHours}
              valueColor="rgba(18, 183, 159, 1)"
            />
            
            <DetailField
              icon={<AccessTimeIcon sx={{ fontSize: 18 }} />}
              label="Time Period"
              value={timePeriod}
              valueColor="rgba(18, 183, 159, 1)"
            />
            
            <DetailField
              icon={<TaskAltIcon sx={{ fontSize: 18 }} />}
              label="Tasks"
              value={
                extractTextFromHTML(taskTitle || "").trim() !== "" ? (
                  extractTextFromHTML(taskTitle)
                ) : (
                  <span style={{ fontStyle: "italic", color:"#959595ff" }}>No Tasks found</span>
                )
              }
            />
            
            <DetailField
              icon={<PersonIcon sx={{ fontSize: 18 }} />}
              label="Created By"
              value={creatorName}
            />
        </Box>

          {/* Right Column */}
          <Box>
            {/* Status Dropdown Field */}
            <Box
            sx={{
                display: "flex",
                alignItems: "center",
                py: 2,
                borderBottom: `1px solid ${isDark ? "rgba(255, 255, 255, 0.08)" : "#f0f0f0"}`,
                minHeight: "65px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "#f5f5f5",
                  color: isDark ? "rgba(255, 255, 255, 0.7)" : "#555555",
                  mr: 2,
                  flexShrink: 0,
                }}
              >
                <EditIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500, flexGrow: 1 }}>
            Status
          </Typography>
              <FormControl size="small" sx={{ width: 130 }}>
            <Select
              key={themeKey}
              value={approvalStatus || ""}
              disabled={!isEditable}
              onChange={(e) => handleUpdateInlineStatus(e, id)}
              input={<OutlinedInput />}
              sx={{
                "& .MuiSelect-select.MuiOutlinedInput-input.MuiInputBase-input": {
                  color: `${getStatusStyle(approvalStatus).color} !important`,
                  backgroundColor: `${getStatusStyle(approvalStatus).backgroundColor} !important`,
                },
                "& .MuiSelect-icon": {
                  color: `${getStatusStyle(approvalStatus).color} !important`,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: `${getStatusStyle(approvalStatus).borderColor} !important`,
                },
          
                /* ⭐ Disabled Styles — Lighter, Greyish */
                "&.Mui-disabled .MuiSelect-select": {
                  backgroundColor: isDark ? "#333333 !important" : "#f5f5f5 !important",
                  color: isDark ? "#777777 !important" : "#9e9e9e !important",
                  borderRadius: "6px",
                },
                "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
                  borderColor: isDark ? "#444444 !important" : "#d6d6d6 !important",
                },
                "&.Mui-disabled .MuiSelect-icon": {
                  color: isDark ? "#555555 !important" : "#b3b3b3 !important",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: "var(--cell-bg-color)",
                    color: "var(--text-color)",
                  },
                },
              }}
            >
              {["APPROVED", "PENDING", "REJECTED"].map((status) => {
                const style = getStatusStyle(status);
                return (
                  <MenuItem
                    key={status}
                    value={status}
                    sx={{
                      color: style.color,
                      "&:hover": {
                        backgroundColor: "var(--hover-bg-color)",
                      },
                    }}
                  >
                    {status.charAt(0) + status.slice(1).toLowerCase()}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>

            <DetailField
              icon={<BusinessIcon sx={{ fontSize: 18 }} />}
              label="Project"
              value={
                <RichText
                  tag="span"
                  style={{ color: "var(--text-color)" }}
                  html={projectTitle || ""}
                />
              }
            />

            <DetailField
              icon={<CalendarMonthIcon sx={{ fontSize: 18 }} />}
              label="Date"
              value={logDate}
            />

            <DetailField
              icon={<HourglassBottomIcon sx={{ fontSize: 18 }} />}
              label="Timesheet Type"
              value={otType}
            />
          </Box>

          {/* Notes Section (Full Width with Icon on Top) */}
          <Box sx={{ mt: 3, gridColumn: { xs: "span 1", md: "span 2" } }}>
            <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 36,
                  height: 36,
                  borderRadius: "8px",
                  backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "#f5f5f5",
                  color: isDark ? "rgba(255, 255, 255, 0.7)" : "#555555",
                  flexShrink: 0,
                }}
              >
                <NotesIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
                Notes
              </Typography>
            </Box>
            
            <Box
              sx={{
                p: 2.5,
                borderRadius: "15px",
                backgroundColor: isDark ? "rgba(255, 255, 255, 0.03)" : "#fafafa",
                border: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #f0f0f0",
                color: isDark ? "rgba(255, 255, 255, 0.6)" : "#666666",
                width: "100%",
                boxSizing: "border-box",
                "& p, & ul, & li, & strong": {
                  color: isDark ? "rgba(255, 255, 255, 0.6) !important" : "#666666 !important",
                }
              }}
            >
              {notes && extractTextFromHTML(notes).trim() !== "" ? (
                <RichText
                  html={notes}
                  style={{
                    textAlign: "left",
                    lineHeight: "1.6",
                  }}
                />
              ) : (
                "No Notes found"
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
export default OTSheetDetail;
